import { PermissionCode, type FleetTelematicsTrip } from '@gb-ticket/shared-types';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { formatDeDateTime } from '@/products/fleet/features/fleet-labels';
import { fleetTableFrameClassName } from '@/products/fleet/features/fleet-panel';
import { FleetTripPurposeBadge } from '@/products/fleet/features/fleet-trip-purpose-badge';
import {
  useFleetTelematicsTrips,
  useSetFleetTripPurpose,
} from '@/products/fleet/features/use-fleet-telematics';

export function FleetTripsTable({ vehicleId }: { vehicleId?: string }) {
  const trips = useFleetTelematicsTrips(vehicleId ? { vehicleId } : undefined);
  const setPurpose = useSetFleetTripPurpose();
  const me = useMe();
  const classify =
    hasPermission(me.data, PermissionCode.FLEET_TELEMATICS_MANAGE) ||
    hasPermission(me.data, PermissionCode.FLEET_TRIPS_CLASSIFY_OWN);

  async function savePurpose(tripId: string, purpose: 'business' | 'private') {
    try {
      await setPurpose.mutateAsync({ tripId, body: { purpose } });
      toast.success('Zweck gespeichert.');
    } catch {
      toast.error('Zweck konnte nicht gespeichert werden.');
    }
  }

  return (
    <div className="space-y-4">
      {trips.isPending ? (
        <p className="text-sm text-muted-foreground">Fahrten werden geladen …</p>
      ) : null}
      {trips.data?.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Keine Fahrten vorhanden.</p>
      ) : (
        <>
          <div className={`hidden md:block ${fleetTableFrameClassName}`}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Start</TableHead>
                  <TableHead>Ende</TableHead>
                  <TableHead>Von</TableHead>
                  <TableHead>Nach</TableHead>
                  <TableHead>Zweck</TableHead>
                  {classify ? <TableHead className="w-40">Aktion</TableHead> : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {trips.data?.items.map((trip) => (
                  <TripRow
                    key={trip.id}
                    trip={trip}
                    classify={classify}
                    onPurpose={(purpose) => savePurpose(trip.id, purpose)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
          <ul className={`divide-y md:hidden ${fleetTableFrameClassName}`}>
            {trips.data?.items.map((trip) => (
              <TripCard
                key={trip.id}
                trip={trip}
                classify={classify}
                onPurpose={(purpose) => savePurpose(trip.id, purpose)}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function TripRow({
  trip,
  classify,
  onPurpose,
}: {
  trip: FleetTelematicsTrip;
  classify: boolean;
  onPurpose: (purpose: 'business' | 'private') => Promise<void>;
}) {
  const canClassify = classify && trip.purpose === 'unset';
  return (
    <TableRow>
      <TableCell>{formatDeDateTime(trip.startedAt)}</TableCell>
      <TableCell>{formatDeDateTime(trip.endedAt)}</TableCell>
      <TableCell>{trip.startPlace ?? '—'}</TableCell>
      <TableCell>{trip.endPlace ?? '—'}</TableCell>
      <TableCell>
        <FleetTripPurposeBadge purpose={trip.purpose} />
      </TableCell>
      {classify ? (
        <TableCell className="space-x-2">
          {canClassify ? (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void onPurpose('business')}
              >
                Dienstlich
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void onPurpose('private')}
              >
                Privat
              </Button>
            </>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </TableCell>
      ) : null}
    </TableRow>
  );
}

function TripCard({
  trip,
  classify,
  onPurpose,
}: {
  trip: FleetTelematicsTrip;
  classify: boolean;
  onPurpose: (purpose: 'business' | 'private') => Promise<void>;
}) {
  const canClassify = classify && trip.purpose === 'unset';
  return (
    <li className="space-y-2 p-3">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium">{formatDeDateTime(trip.startedAt)}</p>
        <FleetTripPurposeBadge purpose={trip.purpose} />
      </div>
      <p className="text-sm text-muted-foreground">bis {formatDeDateTime(trip.endedAt)}</p>
      <p className="text-sm">
        {trip.startPlace ?? '—'} → {trip.endPlace ?? '—'}
      </p>
      {trip.distanceKm != null ? (
        <p className="text-sm text-muted-foreground">
          {trip.distanceKm.toLocaleString('de-DE')} km
        </p>
      ) : null}
      {canClassify ? (
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="min-h-11 flex-1"
            onClick={() => void onPurpose('business')}
          >
            Dienstlich
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="min-h-11 flex-1"
            onClick={() => void onPurpose('private')}
          >
            Privat
          </Button>
        </div>
      ) : null}
    </li>
  );
}
